import React, { useEffect, useState } from 'react';
import './EmailSentNotifier.css';

const DISPLAY_DURATIONS = {
  success: 4000,
  scheduled: 8000,
  summary: 6000,
  safeguard_rejected: 7000,
  scorer_rejected: 7000,
  blocked: 5000,
  blacklisted: 5000,
  lead_added: 4000,
};

const EmailSentNotifier = ({ status, message: rawMessage, onClose, scheduledTime, recipientEmail, senderEmail }) => {
  const message = (typeof rawMessage === 'object' && rawMessage !== null)
    ? (rawMessage.message || '')
    : (rawMessage || '');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status) {
      setVisible(true);
      
      const displayDuration = (scheduledTime && status === 'success')
        ? DISPLAY_DURATIONS.scheduled
        : (DISPLAY_DURATIONS[status] || DISPLAY_DURATIONS.success);
      
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          if (onClose) onClose();
        }, 300);
      }, displayDuration);
      
      return () => clearTimeout(timer);
    }
  }, [status, onClose, scheduledTime]);

  if (!status) return null;

  const formatScheduledDate = (timestamp) => {
    try {
      const date = new Date(parseInt(timestamp));
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}. ${month} ${year}, ${hours}:${minutes}h`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  const emailAddress = recipientEmail || '';

  return (
    <div className={`email-sent-notifier ${status} ${visible ? 'visible' : 'hidden'} ${scheduledTime ? 'scheduled' : ''}`}>
      <div className="email-sent-notifier-icon">
        {status === 'success' ? (
          scheduledTime ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )
        ) : status === 'summary' ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 12h6M9 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (status === 'safeguard_rejected' || status === 'scorer_rejected') ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (status === 'blacklisted' || status === 'blocked') ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ) : status === 'lead_added' ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9v2m0 4h.01M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="email-sent-notifier-content">
        <p className="email-sent-notifier-title">
          {status === 'success' ? 
            (scheduledTime ? `Email scheduled${emailAddress ? ` `  : ''}` : 'Email sent') : 
            status === 'summary' ? 'Summary' : 
            status === 'safeguard_rejected' ? 'SafeGuard Rejected' : 
            status === 'scorer_rejected' ? 'Quality Check Failed' : 
            status === 'blocked' ? 'Contact Blocked' :
            status === 'blacklisted' ? 'Email Blacklisted' : 
            status === 'lead_added' ? 'Added to Leads' : 'Email error'}
          {emailAddress && scheduledTime && 
            <span className="email-address-display">({emailAddress})</span>
          }
        </p>
        <p
          className="email-sent-notifier-message"
          title={[message, senderEmail ? `from ${senderEmail}` : null]
            .filter(Boolean)
            .join(' · ')}
        >
          <span className="email-sent-notifier-message-text">{message}</span>
          {senderEmail && (
            <span className="sender-email-info">
              <span className="sender-email-sep" aria-hidden="true">·</span>
              from <strong>{senderEmail}</strong>
            </span>
          )}
          {scheduledTime && status === 'success' && (
            <span className="scheduled-time-info">
              <span className="sender-email-sep" aria-hidden="true">·</span>
              <strong>scheduled for {formatScheduledDate(scheduledTime)}</strong>
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default EmailSentNotifier;